import {
  $applyNodeReplacement,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';

import type { NoteEditorBlockDef } from '../../../config';

import { TemplateFieldHost } from './templateFieldWidgets';

type SerializedTemplateFieldNode = Spread<
  { def: NoteEditorBlockDef },
  SerializedLexicalNode
>;

const EMPTY_DEF: NoteEditorBlockDef = {
  ID: '',
  SlotID: '',
  Kind: { case: undefined },
};

const markField = (dom: HTMLElement, def: NoteEditorBlockDef) => {
  dom.dataset.noteBlockId = def.ID;
  dom.dataset.templateField = def.Kind.case ?? '';
  return dom;
};

// A field of the template in its line: one unit of the text, which holds its
// block's definition; its widget edits what the field offers.
export class TemplateFieldNode extends DecoratorNode<JSX.Element> {
  __def: NoteEditorBlockDef;

  static getType(): string {
    return 'template-field';
  }

  static clone(node: TemplateFieldNode): TemplateFieldNode {
    return new TemplateFieldNode(node.__def, node.__key);
  }

  static importJSON(json: SerializedTemplateFieldNode): TemplateFieldNode {
    return new TemplateFieldNode(json.def).updateFromJSON(json);
  }

  constructor(def: NoteEditorBlockDef = EMPTY_DEF, key?: NodeKey) {
    super(key);
    this.__def = def;
  }

  exportJSON(): SerializedTemplateFieldNode {
    return { ...super.exportJSON(), def: this.getDef() };
  }

  createDOM(): HTMLElement {
    return markField(document.createElement('span'), this.__def);
  }

  updateDOM(previous: TemplateFieldNode, dom: HTMLElement): boolean {
    if (previous.__def !== this.__def) {
      markField(dom, this.__def);
    }
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  decorate(): JSX.Element {
    return (
      <TemplateFieldHost kind="field" nodeKey={this.__key} def={this.__def} />
    );
  }

  getDef(): NoteEditorBlockDef {
    return this.getLatest().__def;
  }

  getBlockId(): string {
    return this.getLatest().__def.ID;
  }

  setDef(def: NoteEditorBlockDef): this {
    const writable = this.getWritable();
    writable.__def = def;
    return writable;
  }
}

export const $createTemplateFieldNode = (def: NoteEditorBlockDef) =>
  $applyNodeReplacement(new TemplateFieldNode(def));

export const $isTemplateFieldNode = (
  node: LexicalNode | null | undefined,
): node is TemplateFieldNode => node instanceof TemplateFieldNode;
